import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import { Label } from '../components/ui/label';
import { ArrowLeft, Save, Send, CheckCircle2, AlertCircle, Wifi, WifiOff, Camera, Upload, X, Image, ScanLine, Loader2 } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { v4 as uuidv4 } from 'uuid';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Offline storage key
const OFFLINE_FORMS_KEY = 'rogers_offline_forms';

export default function FormFillPage() {
  const { templateId } = useParams();
  const navigate = useNavigate();
  const [template, setTemplate] = useState(null);
  const [formData, setFormData] = useState({});
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [errors, setErrors] = useState({});
  const [uploadedImages, setUploadedImages] = useState({});
  const [uploadingImage, setUploadingImage] = useState(null);
  const [extractingOCR, setExtractingOCR] = useState(false);
  const fileInputRefs = useRef({});
  const ocrInputRef = useRef(null);

  useEffect(() => {
    fetchTemplate();
    
    // Online/offline listeners
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, [templateId]);

  // Sync offline forms when coming back online
  useEffect(() => {
    if (isOnline) {
      syncOfflineForms();
    }
  }, [isOnline]);

  const fetchTemplate = async () => {
    try {
      const token = localStorage.getItem('vaultai_token');
      const headers = { Authorization: `Bearer ${token}` };
      
      const res = await axios.get(`${API}/forms/templates/${templateId}`, { headers });
      setTemplate(res.data);
      
      // Initialize form data with defaults
      const initialData = {};
      res.data.sections?.forEach(section => {
        section.fields?.forEach(field => {
          if (field.type === 'multiselect' || field.type === 'checkbox') {
            initialData[field.id] = [];
          } else if (field.type === 'date') {
            // Use BC (America/Vancouver) local date so it doesn't roll over to tomorrow's UTC
            const bcParts = new Intl.DateTimeFormat('en-CA', {
              timeZone: 'America/Vancouver',
              year: 'numeric', month: '2-digit', day: '2-digit'
            }).formatToParts(new Date()).reduce((acc, p) => {
              if (p.type !== 'literal') acc[p.type] = p.value;
              return acc;
            }, {});
            initialData[field.id] = `${bcParts.year}-${bcParts.month}-${bcParts.day}`;
          } else if (field.type === 'time') {
            // BC local time
            const bcTimeParts = new Intl.DateTimeFormat('en-CA', {
              timeZone: 'America/Vancouver',
              hour: '2-digit', minute: '2-digit', hour12: false
            }).formatToParts(new Date()).reduce((acc, p) => {
              if (p.type !== 'literal') acc[p.type] = p.value;
              return acc;
            }, {});
            initialData[field.id] = `${bcTimeParts.hour}:${bcTimeParts.minute}`;
          } else {
            initialData[field.id] = field.default_value || '';
          }
        });
      });
      
      // Pre-fill name from user
      const userData = JSON.parse(localStorage.getItem('user') || '{}');
      if (userData.name) initialData.name = userData.name;
      if (userData.email) initialData.email = userData.email;
      
      setFormData(initialData);
    } catch (err) {
      console.error('Failed to fetch template:', err);
      toast.error('Failed to load form');
    } finally {
      setLoading(false);
    }
  };

  const handleFieldChange = (fieldId, value) => {
    setFormData(prev => ({ ...prev, [fieldId]: value }));
    // Clear error when field is modified
    if (errors[fieldId]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[fieldId];
        return newErrors;
      });
    }
  };

  const handleMultiSelectToggle = (fieldId, value) => {
    setFormData(prev => {
      const current = prev[fieldId] || [];
      if (current.includes(value)) {
        return { ...prev, [fieldId]: current.filter(v => v !== value) };
      } else {
        return { ...prev, [fieldId]: [...current, value] };
      }
    });
  };

  const validateForm = () => {
    const newErrors = {};
    template.sections?.forEach(section => {
      section.fields?.forEach(field => {
        if (field.required) {
          const value = formData[field.id];
          if (field.type === 'multiselect' || field.type === 'checkbox') {
            if (!value || value.length === 0) {
              newErrors[field.id] = 'Please select at least one option';
            }
          } else if (!value || value.toString().trim() === '') {
            newErrors[field.id] = 'This field is required';
          }
        }
      });
    });
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const saveOffline = (isDraft = false) => {
    const offlineData = {
      offline_id: uuidv4(),
      template_id: templateId,
      template_name: template.name,
      data: formData,
      is_draft: isDraft,
      submitted_at: new Date().toISOString(),
      synced: false
    };
    
    const existing = JSON.parse(localStorage.getItem(OFFLINE_FORMS_KEY) || '[]');
    existing.push(offlineData);
    localStorage.setItem(OFFLINE_FORMS_KEY, JSON.stringify(existing));
    
    return offlineData;
  };

  const syncOfflineForms = async () => {
    const offlineForms = JSON.parse(localStorage.getItem(OFFLINE_FORMS_KEY) || '[]');
    const unsynced = offlineForms.filter(f => !f.synced);
    
    if (unsynced.length === 0) return;
    
    try {
      const token = localStorage.getItem('vaultai_token');
      const headers = { Authorization: `Bearer ${token}` };
      
      const res = await axios.post(`${API}/forms/submissions/sync`, unsynced, { headers });
      
      // Mark synced forms
      const updated = offlineForms.map(f => {
        const result = res.data.results?.find(r => r.offline_id === f.offline_id);
        if (result && (result.status === 'synced' || result.status === 'already_synced')) {
          return { ...f, synced: true, server_id: result.id };
        }
        return f;
      });
      
      localStorage.setItem(OFFLINE_FORMS_KEY, JSON.stringify(updated));
      
      if (res.data.synced > 0) {
        toast.success(`${res.data.synced} offline form(s) synced`);
      }
    } catch (err) {
      console.error('Sync failed:', err);
    }
  };

  const handleImageUpload = async (fieldId, file) => {
    if (!file) return;
    
    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/heic', 'image/heif'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Please upload a valid image (JPEG, PNG, WebP, or HEIC)');
      return;
    }
    
    // Check file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      toast.error('Image too large. Maximum size is 10MB');
      return;
    }
    
    setUploadingImage(fieldId);
    
    try {
      // Create preview immediately
      const reader = new FileReader();
      reader.onload = (e) => {
        setUploadedImages(prev => ({
          ...prev,
          [fieldId]: { preview: e.target.result, file, uploaded: false }
        }));
      };
      reader.readAsDataURL(file);
      
      // Upload to server if online
      if (isOnline) {
        const token = localStorage.getItem('vaultai_token');
        const formDataUpload = new FormData();
        formDataUpload.append('file', file);
        
        const res = await axios.post(`${API}/forms/upload-image`, formDataUpload, {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        });
        
        // Store the image ID in form data
        handleFieldChange(fieldId, res.data.image_id);
        
        // Build the image URL for AI extraction
        const imageUrl = `${API}/forms/images/${res.data.image_id}?token=${token}`;
        
        setUploadedImages(prev => ({
          ...prev,
          [fieldId]: { ...prev[fieldId], uploaded: true, imageId: res.data.image_id, imageUrl }
        }));
        toast.success('Image uploaded successfully');
      } else {
        // Store locally for offline sync
        toast.info('Image saved locally - will upload when online');
      }
    } catch (err) {
      console.error('Image upload failed:', err);
      toast.error('Failed to upload image');
    } finally {
      setUploadingImage(null);
    }
  };
  
  const removeImage = (fieldId) => {
    setUploadedImages(prev => {
      const newImages = { ...prev };
      delete newImages[fieldId];
      return newImages;
    });
    handleFieldChange(fieldId, '');
  };

  // OCR extraction for BOL photos
  const handleOCRExtract = async (file) => {
    if (!file) return;
    
    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/heic', 'image/heif'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Please upload a valid image (JPEG, PNG, WebP, or HEIC)');
      return;
    }
    
    setExtractingOCR(true);
    toast.info('Extracting data from BOL image... This may take a moment.');
    
    try {
      const token = localStorage.getItem('vaultai_token');
      const formDataUpload = new FormData();
      formDataUpload.append('file', file);
      
      const res = await axios.post(`${API}/forms/ocr-bol`, formDataUpload, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      
      if (res.data.success && res.data.extracted_data) {
        // Pre-fill form with extracted data
        const extracted = res.data.extracted_data;
        setFormData(prev => ({
          ...prev,
          ...Object.fromEntries(
            Object.entries(extracted).filter(([_, v]) => v !== '' && v !== null)
          )
        }));
        
        // Store the image reference
        if (res.data.image_id) {
          handleFieldChange('bol_photo', res.data.image_id);
          
          // Create preview
          const reader = new FileReader();
          reader.onload = (e) => {
            setUploadedImages(prev => ({
              ...prev,
              bol_photo: { preview: e.target.result, uploaded: true, imageId: res.data.image_id }
            }));
          };
          reader.readAsDataURL(file);
        }
        
        toast.success('BOL data extracted! Please review and correct any errors.');
      } else {
        toast.error(res.data.error || 'Could not extract data from image');
      }
    } catch (err) {
      console.error('OCR extraction failed:', err);
      toast.error('Failed to extract data from image. Please enter manually.');
    } finally {
      setExtractingOCR(false);
    }
  };

  // AI extraction for Scale Receipt photos
  const handleScaleReceiptExtract = async (imageId, previewBase64) => {
    if (!imageId && !previewBase64) {
      toast.error('No image available for extraction');
      return;
    }
    
    setExtractingOCR(true);
    toast.info('Extracting data from scale receipt... This may take a moment.');
    
    try {
      const token = localStorage.getItem('vaultai_token');
      
      // Build request body. Prefer sending image_base64 directly (already on
      // device) so we don't force the server to re-fetch the image from slow
      // Atlas storage. Fall back to image_id if base64 isn't available.
      const requestBody = {};
      if (previewBase64) {
        // Extract base64 from data URL if needed
        const base64Data = previewBase64.includes(',') ? previewBase64.split(',')[1] : previewBase64;
        requestBody.image_base64 = base64Data;
      } else if (imageId) {
        requestBody.image_id = imageId;
      }
      
      const res = await axios.post(`${API}/scale-receipt/extract`,
        requestBody,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          // OpenAI vision can take 30-60s on busy days. Give it room before
          // axios kills the request — much better than a generic toast error.
          timeout: 90000,
        }
      );

      if (res.data.success && res.data.extracted_data) {
        // Pre-fill form with extracted data
        const extracted = res.data.extracted_data;
        setFormData(prev => ({
          ...prev,
          ...Object.fromEntries(
            Object.entries(extracted).filter(([_, v]) => v !== '' && v !== null && v !== undefined)
          )
        }));

        toast.success('Scale receipt data extracted! Please review and correct any errors.');
      } else {
        toast.error(res.data.error || 'Could not extract data from image');
      }
    } catch (err) {
      console.error('Scale receipt extraction failed:', err);
      // Surface the real server message when available so the user knows
      // whether to retry vs enter manually.
      const serverMsg = err.response?.data?.detail || err.response?.data?.error;
      if (serverMsg) {
        toast.error(`Extraction failed: ${serverMsg}`);
      } else if (err.code === 'ECONNABORTED') {
        toast.error('Extraction timed out. Please try again or enter manually.');
      } else {
        toast.error('Failed to extract data from image. Please enter manually.');
      }
    } finally {
      setExtractingOCR(false);
    }
  };

  const handleSubmit = async (isDraft = false) => {
    if (!isDraft && !validateForm()) {
      toast.error('Please fill in all required fields');
      return;
    }

    setSubmitting(true);

    try {
      if (!isOnline) {
        // Save offline
        saveOffline(isDraft);
        toast.success(isDraft ? 'Draft saved offline' : 'Form saved offline - will sync when online');
        if (!isDraft) navigate('/dashboard/forms');
        return;
      }

      const token = localStorage.getItem('vaultai_token');
      const headers = { Authorization: `Bearer ${token}` };

      const submission = {
        template_id: templateId,
        data: formData,
        is_draft: isDraft,
        location: null // Could add GPS here
      };

      await axios.post(`${API}/forms/submissions`, submission, { headers });
      
      toast.success(isDraft ? 'Draft saved' : 'Form submitted successfully');
      if (!isDraft) navigate('/dashboard/forms');
    } catch (err) {
      console.error('Submit failed:', err);
      
      // Save offline if network error
      if (err.code === 'ERR_NETWORK') {
        saveOffline(isDraft);
        toast.success('Form saved offline - will sync when online');
        if (!isDraft) navigate('/dashboard/forms');
      } else {
        toast.error('Failed to submit form');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const renderField = (field) => {
    const error = errors[field.id];
    
    switch (field.type) {
      case 'text':
      case 'email':
      case 'phone':
        return (
          <div key={field.id} className="space-y-2">
            <Label className={field.required ? 'after:content-["*"] after:text-red-500 after:ml-1' : ''}>
              {field.label}
            </Label>
            <Input
              type={field.type === 'email' ? 'email' : field.type === 'phone' ? 'tel' : 'text'}
              value={formData[field.id] || ''}
              onChange={(e) => handleFieldChange(field.id, e.target.value)}
              placeholder={field.placeholder}
              className={error ? 'border-red-500' : ''}
            />
            {error && <p className="text-red-500 text-xs">{error}</p>}
          </div>
        );

      case 'textarea':
        return (
          <div key={field.id} className="space-y-2">
            <Label className={field.required ? 'after:content-["*"] after:text-red-500 after:ml-1' : ''}>
              {field.label}
            </Label>
            <Textarea
              value={formData[field.id] || ''}
              onChange={(e) => handleFieldChange(field.id, e.target.value)}
              placeholder={field.placeholder}
              rows={3}
              className={error ? 'border-red-500' : ''}
            />
            {error && <p className="text-red-500 text-xs">{error}</p>}
          </div>
        );

      case 'date':
        return (
          <div key={field.id} className="space-y-2">
            <Label className={field.required ? 'after:content-["*"] after:text-red-500 after:ml-1' : ''}>
              {field.label}
            </Label>
            <Input
              type="date"
              value={formData[field.id] || ''}
              onChange={(e) => handleFieldChange(field.id, e.target.value)}
              className={error ? 'border-red-500' : ''}
            />
            {error && <p className="text-red-500 text-xs">{error}</p>}
          </div>
        );

      case 'time':
        return (
          <div key={field.id} className="space-y-2">
            <Label className={field.required ? 'after:content-["*"] after:text-red-500 after:ml-1' : ''}>
              {field.label}
            </Label>
            <Input
              type="time"
              value={formData[field.id] || ''}
              onChange={(e) => handleFieldChange(field.id, e.target.value)}
              className={error ? 'border-red-500' : ''}
            />
            {error && <p className="text-red-500 text-xs">{error}</p>}
          </div>
        );

      case 'number':
        // Special handling for ClearSky Fuel Log variance display
        const isClearSkyForm = template?.name === 'ClearSky Fuel Log';
        const isDieselField = field.id === 'diesel_added';
        const isClearSkyField = field.id === 'clearsky_added';
        
        // Calculate expected ClearSky and variance
        let expectedClearSky = null;
        let variance = null;
        let variancePercent = null;
        
        if (isClearSkyForm && isClearSkyField && formData.diesel_added) {
          expectedClearSky = parseFloat(formData.diesel_added) * 0.25;
          const actualClearSky = parseFloat(formData[field.id] || 0);
          if (actualClearSky > 0) {
            variance = actualClearSky - expectedClearSky;
            variancePercent = ((variance / expectedClearSky) * 100).toFixed(1);
          }
        }
        
        return (
          <div key={field.id} className="space-y-2">
            <Label className={field.required ? 'after:content-["*"] after:text-red-500 after:ml-1' : ''}>
              {field.label}
            </Label>
            <Input
              type="number"
              step={field.step || "any"}
              min={field.min}
              max={field.max}
              value={formData[field.id] || ''}
              onChange={(e) => handleFieldChange(field.id, e.target.value)}
              placeholder={field.placeholder}
              className={`${error ? 'border-red-500' : ''} ${variance > 0 ? 'border-yellow-500' : ''}`}
            />
            {field.helper_text && (
              <p className="text-muted-foreground text-xs">{field.helper_text}</p>
            )}
            {/* Show expected amount for ClearSky field */}
            {isClearSkyForm && isClearSkyField && formData.diesel_added && (
              <div className="text-xs space-y-1">
                <p className="text-green-400 font-medium">
                  You should add: {expectedClearSky.toFixed(2)} mL
                </p>
                {variance !== null && variance > 5 && (
                  <p className="text-yellow-500 font-medium flex items-center gap-1">
                    <AlertCircle className="h-3 w-3" />
                    Over by {variance.toFixed(2)} mL (+{variancePercent}%)
                  </p>
                )}
                {variance !== null && variance < -5 && (
                  <p className="text-blue-400 text-xs">
                    Under by {Math.abs(variance).toFixed(2)} mL ({variancePercent}%)
                  </p>
                )}
              </div>
            )}
            {error && <p className="text-red-500 text-xs">{error}</p>}
          </div>
        );

      case 'combobox':
        // Combobox - dropdown with ability to type custom value
        return (
          <div key={field.id} className="space-y-2">
            <Label className={field.required ? 'after:content-["*"] after:text-red-500 after:ml-1' : ''}>
              {field.label}
            </Label>
            <div className="relative">
              <Input
                list={`${field.id}-options`}
                value={formData[field.id] || ''}
                onChange={(e) => handleFieldChange(field.id, e.target.value)}
                placeholder={field.placeholder || 'Select or type...'}
                className={error ? 'border-red-500' : ''}
              />
              <datalist id={`${field.id}-options`}>
                {field.options?.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </datalist>
            </div>
            {error && <p className="text-red-500 text-xs">{error}</p>}
          </div>
        );

      case 'select':
        return (
          <div key={field.id} className="space-y-2">
            <Label className={field.required ? 'after:content-["*"] after:text-red-500 after:ml-1' : ''}>
              {field.label}
            </Label>
            <select
              value={formData[field.id] || ''}
              onChange={(e) => handleFieldChange(field.id, e.target.value)}
              className={`flex h-10 w-full rounded-md border bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${error ? 'border-red-500' : 'border-input'}`}
            >
              <option value="">Select an option...</option>
              {field.options?.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
            {error && <p className="text-red-500 text-xs">{error}</p>}
          </div>
        );

      case 'multiselect':
      case 'checkbox':
        return (
          <div key={field.id} className="space-y-3">
            <Label className={field.required ? 'after:content-["*"] after:text-red-500 after:ml-1' : ''}>
              {field.label}
            </Label>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              {field.options?.map((option) => (
                <label
                  key={option.value}
                  className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                    (formData[field.id] || []).includes(option.value)
                      ? 'bg-primary/10 border-primary'
                      : 'bg-secondary/30 border-border hover:bg-secondary/50'
                  }`}
                >
                  <Checkbox
                    checked={(formData[field.id] || []).includes(option.value)}
                    onCheckedChange={() => handleMultiSelectToggle(field.id, option.value)}
                  />
                  <span className="text-sm">{option.label}</span>
                </label>
              ))}
            </div>
            {error && <p className="text-red-500 text-xs">{error}</p>}
          </div>
        );

      case 'image':
        const imageData = uploadedImages[field.id];
        const isScaleReceipt = template?.name === 'Scale Receipt';
        const canExtract = isScaleReceipt && field.id === 'receipt_photo' && imageData?.preview;
        
        return (
          <div key={field.id} className="space-y-3">
            <Label className={field.required ? 'after:content-["*"] after:text-red-500 after:ml-1' : ''}>
              {field.label}
            </Label>
            
            {imageData?.preview ? (
              <div className="relative">
                <img 
                  src={imageData.preview} 
                  alt="Uploaded" 
                  className="max-w-full max-h-64 rounded-lg border object-contain"
                />
                <div className="absolute top-2 right-2 flex gap-2">
                  {imageData.uploaded && (
                    <span className="bg-green-500 text-white text-xs px-2 py-1 rounded-full flex items-center gap-1">
                      <CheckCircle2 className="h-3 w-3" /> Uploaded
                    </span>
                  )}
                  <Button
                    type="button"
                    variant="destructive"
                    size="sm"
                    onClick={() => removeImage(field.id)}
                    className="h-8 w-8 p-0"
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
                {/* Extract Data button for Scale Receipt */}
                {canExtract && (
                  <div className="mt-3">
                    <Button
                      type="button"
                      onClick={() => handleScaleReceiptExtract(imageData.imageId, imageData.preview)}
                      disabled={extractingOCR || !imageData.uploaded}
                      className="w-full bg-blue-600 hover:bg-blue-700"
                    >
                      {extractingOCR ? (
                        <>
                          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                          Extracting Data...
                        </>
                      ) : (
                        <>
                          <ScanLine className="h-4 w-4 mr-2" />
                          Extract Data from Image
                        </>
                      )}
                    </Button>
                  </div>
                )}
              </div>
            ) : (
              <div className="border-2 border-dashed border-border rounded-lg p-6 text-center">
                <input
                  type="file"
                  accept="image/*"
                  capture="environment"
                  ref={el => fileInputRefs.current[field.id] = el}
                  onChange={(e) => handleImageUpload(field.id, e.target.files[0])}
                  className="hidden"
                  data-testid={`image-input-${field.id}`}
                />
                <div className="flex flex-col items-center gap-3">
                  <Image className="h-10 w-10 text-muted-foreground" />
                  <p className="text-sm text-muted-foreground">
                    {isScaleReceipt ? 'Upload scale receipt image for AI extraction' : 'Take a photo or upload an image'}
                  </p>
                  <div className="flex gap-2">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => fileInputRefs.current[field.id]?.click()}
                      disabled={uploadingImage === field.id}
                      data-testid={`upload-btn-${field.id}`}
                    >
                      {uploadingImage === field.id ? (
                        <div className="animate-spin rounded-full h-4 w-4 border-2 border-primary border-t-transparent mr-2" />
                      ) : (
                        <Camera className="h-4 w-4 mr-2" />
                      )}
                      {uploadingImage === field.id ? 'Uploading...' : 'Take Photo'}
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        const input = fileInputRefs.current[field.id];
                        if (input) {
                          input.removeAttribute('capture');
                          input.click();
                          // Restore capture attribute for mobile
                          setTimeout(() => input.setAttribute('capture', 'environment'), 100);
                        }
                      }}
                      disabled={uploadingImage === field.id}
                    >
                      <Upload className="h-4 w-4 mr-2" />
                      Browse Files
                    </Button>
                  </div>
                </div>
              </div>
            )}
            {error && <p className="text-red-500 text-xs">{error}</p>}
          </div>
        );

      default:
        return null;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!template) {
    return (
      <div className="text-center py-12">
        <p className="text-muted-foreground">Form not found</p>
        <Button variant="outline" onClick={() => navigate('/dashboard/forms')} className="mt-4">
          Back to Forms
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-4xl mx-auto" data-testid="form-fill-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="outline" onClick={() => navigate('/dashboard/forms')} className="flex items-center gap-2">
            <ArrowLeft className="h-4 w-4" />
            Back
          </Button>
          <div>
            <h1 className="text-xl font-bold text-foreground">{template.name}</h1>
            <p className="text-sm text-muted-foreground">{template.description}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {isOnline ? (
            <span className="flex items-center gap-1 text-xs text-green-500">
              <Wifi className="h-4 w-4" /> Online
            </span>
          ) : (
            <span className="flex items-center gap-1 text-xs text-amber-500">
              <WifiOff className="h-4 w-4" /> Offline
            </span>
          )}
        </div>
      </div>

      {/* OCR Scan Section for BOL */}
      {template.name?.toLowerCase().includes('bill of lading') && (
        <Card className="border-blue-500/30 bg-blue-500/5">
          <CardContent className="p-6">
            <div className="flex items-start gap-4">
              <div className="p-3 rounded-lg bg-blue-500/20">
                <ScanLine className="h-6 w-6 text-blue-400" />
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-foreground">Quick Fill: Scan Paper BOL</h3>
                <p className="text-sm text-muted-foreground mt-1">
                  Take a photo of a completed paper Bill of Lading and we&apos;ll extract the data automatically
                </p>
                <div className="flex gap-2 mt-4">
                  <input
                    type="file"
                    accept="image/*"
                    capture="environment"
                    ref={ocrInputRef}
                    onChange={(e) => handleOCRExtract(e.target.files[0])}
                    className="hidden"
                    data-testid="ocr-input"
                  />
                  <Button
                    type="button"
                    variant="default"
                    className="bg-blue-600 hover:bg-blue-700"
                    onClick={() => ocrInputRef.current?.click()}
                    disabled={extractingOCR}
                    data-testid="ocr-scan-btn"
                  >
                    {extractingOCR ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Extracting Data...
                      </>
                    ) : (
                      <>
                        <Camera className="h-4 w-4 mr-2" />
                        Scan BOL Photo
                      </>
                    )}
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => {
                      if (ocrInputRef.current) {
                        ocrInputRef.current.removeAttribute('capture');
                        ocrInputRef.current.click();
                        setTimeout(() => ocrInputRef.current?.setAttribute('capture', 'environment'), 100);
                      }
                    }}
                    disabled={extractingOCR}
                  >
                    <Upload className="h-4 w-4 mr-2" />
                    Browse Files
                  </Button>
                </div>
                {extractingOCR && (
                  <p className="text-xs text-blue-400 mt-2">
                    AI is reading your BOL... This usually takes 5-10 seconds.
                  </p>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Form Sections */}
      {template.sections?.sort((a, b) => a.order - b.order).map((section) => (
        <Card key={section.id}>
          <CardHeader className="pb-3">
            <CardTitle className="text-lg">{section.title}</CardTitle>
            {section.description && (
              <p className="text-sm text-muted-foreground">{section.description}</p>
            )}
          </CardHeader>
          <CardContent className="space-y-4">
            {section.fields?.sort((a, b) => a.order - b.order).map(renderField)}
          </CardContent>
        </Card>
      ))}

      {/* Submit Buttons */}
      <div className="flex items-center justify-between pt-4 border-t">
        <Button
          variant="outline"
          onClick={() => handleSubmit(true)}
          disabled={submitting}
        >
          <Save className="h-4 w-4 mr-2" />
          Save Draft
        </Button>
        <Button
          onClick={() => handleSubmit(false)}
          disabled={submitting}
          className="min-w-[140px]"
        >
          {submitting ? (
            <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
          ) : (
            <>
              <Send className="h-4 w-4 mr-2" />
              Submit Form
            </>
          )}
        </Button>
      </div>
    </div>
  );
}
